/**
 * Segmented Control Component
 *
 * Toggle between Original and Revised message views.
 */

import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import type { SegmentedControlProps, ViewMode } from './types';
import { styles } from './inlineAnnotationsStyles';

/**
 * SegmentedControl - Original/Revised view toggle
 */
export function SegmentedControl({
  value,
  onChange,
  hasChanges,
  testID,
}: SegmentedControlProps) {
  const { t } = useTranslation('features');

  const segments: { key: ViewMode; label: string }[] = [
    { key: 'original', label: t('inline_annotations.view_original', 'Original') },
    { key: 'revised', label: t('inline_annotations.view_revised', 'Revised') },
  ];

  return (
    <View style={styles.segmentedControl} testID={testID}>
      {segments.map((segment) => {
        const isActive = value === segment.key;
        const showIndicator = segment.key === 'revised' && hasChanges;

        return (
          <Pressable
            key={segment.key}
            style={[
              styles.segmentButton,
              isActive && styles.segmentButtonActive,
            ]}
            onPress={() => onChange(segment.key)}
            accessibilityRole="tab"
            accessibilityState={{ selected: isActive }}
            accessibilityLabel={segment.label}
            testID={`${testID}-${segment.key}`}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Text
                style={[
                  styles.segmentText,
                  isActive && styles.segmentTextActive,
                ]}
              >
                {segment.label}
              </Text>
              {showIndicator && <View style={styles.changeIndicator} />}
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}

export default SegmentedControl;
